import { DateTime } from 'luxon';

import {
  CheckMetadata,
  createSimpleCheckMetadataArray,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import { formatDate } from 'dashboard/lib/utils/formatters';
import { CheckStatus, IdExpiredDetection } from 'dashboard/models/check';

export const computeIdExpiredDetectionMetadata = (check: IdExpiredDetection): CheckMetadata => {
  const {
    'submitted-date': submittedDate,
    'expiration-date': govIdExpirationDate,
    'accepted-until-date': acceptedUntilDate,
    'grace-period-days': gracePeriodDays,
  } = check.metadata || {};

  const metadata: Array<{ label: string; value: React.ReactNode; labelHelpText?: string }> = [
    {
      label: 'ID Expiration Date',
      value: formatDate(govIdExpirationDate) || '-',
    },
  ];

  // If check passes when submitted date is 1 day after check date date, then server included a
  // buffer to account for timezone difference between client time and server UTC time. We show
  // a special message to explain this! See https://en.wikipedia.org/wiki/Anywhere_on_Earth.
  const passedWithAnywhereOnEarthBuffer =
    check.status === CheckStatus.Passed &&
    submittedDate &&
    acceptedUntilDate &&
    Math.floor(
      DateTime.fromISO(submittedDate).diff(DateTime.fromISO(acceptedUntilDate), 'days').days,
    ) === 1;

  const showAcceptedUntilAttribute =
    acceptedUntilDate != null && (!!gracePeriodDays || passedWithAnywhereOnEarthBuffer);

  if (showAcceptedUntilAttribute) {
    let gracePeriodMessage;
    if (gracePeriodDays && gracePeriodDays !== 0) {
      gracePeriodMessage =
        gracePeriodDays > 0
          ? `Includes a grace period of ${gracePeriodDays} day(s)`
          : `Must be submitted ${Math.abs(gracePeriodDays)} day(s) before expiration`;
    }

    // Show message explaining that check passed due to Anywhere on Earth buffer.
    let passedWithAnywhereOnEarthBufferMessage;
    if (passedWithAnywhereOnEarthBuffer) {
      passedWithAnywhereOnEarthBufferMessage = 'Accepted until this date Anywhere on Earth (AoE).';
    }

    const acceptedUntilHelpIconMessage = [
      gracePeriodMessage,
      passedWithAnywhereOnEarthBufferMessage,
    ]
      .filter(Boolean)
      .join('. ');

    metadata.push({
      label: 'Accepted Until',
      value: `${formatDate(acceptedUntilDate) || '-'}${
        passedWithAnywhereOnEarthBuffer ? ' (AoE)' : ''
      }`,
      labelHelpText: acceptedUntilHelpIconMessage,
    });
  }

  metadata.push({
    label: 'Check Run Date',
    value: `${formatDate(submittedDate) || '-'}${passedWithAnywhereOnEarthBuffer ? ' (AoE)' : ''}`,
  });

  return {
    metadata: createSimpleCheckMetadataArray(metadata),
    hideIfVerificationPassed: false,
  };
};
